import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import usePageStylesheet from '../util/use-page-stylesheet';
import useMintFlow from '../util/use-mint-flow';
import EyesNav from '../components/EyesNav';
import EyesFooter from '../components/EyesFooter';
import EyeViewer from '../components/EyeViewer';
import MintDialogs from '../components/MintDialogs';
import Connect from '../components/Connect';

/**
 * The landing page, ported 1:1 from eyes/index.html.
 *
 * Markup, styles and behaviour are the originals: the stylesheet lives at
 * public/eyes/eyes.css and the scripts that shipped inline in the document
 * are the effects below, with teardown added so they do not outlive the
 * route. The tree is rendered through a portal because the original CSS
 * targets `body > nav` and expects the sections to be children of <body>.
 */
export default function Splash() {
  usePageStylesheet('/eyes/eyes.css');

  // MINT NOW mints here rather than routing to /mint: no wallet yet means the
  // wallet picker, a connected wallet means the mint form.
  const wallet = useSelector((state) => state.wallet);
  const flow = useMintFlow();
  const [showConnect, setShowConnect] = useState(false);
  // Set when the mint CTA is what opened the wallet picker, so the mint form
  // follows on its own once a wallet is connected.
  const [mintAfterConnect, setMintAfterConnect] = useState(false);

  // Live supply for the mint banner; deployCount is refreshed from the API by
  // useMintFlow on mount and after every mint.
  const minted = Number(flow.deployCount) || 0;
  const remaining = Math.max(0, flow.deployLimit - minted);
  const mintedPct = Math.min(100, (minted / flow.deployLimit) * 100);

  // /mint shows COMING SOON until the start date; the CTA here says the same
  // rather than opening a form that cannot be submitted.
  const openMintForm = () => {
    if (!flow.mintFlag) {
      toast.error('Mint has not started yet!');
      return;
    }
    flow.handleMintForm();
  };

  const handleMintClick = (e) => {
    e.preventDefault();
    // The dialog marks everything outside itself aria-hidden, and the browser
    // refuses to do that while a descendant still holds focus - which this
    // link does, since clicking it no longer navigates away.
    if (e.currentTarget && e.currentTarget.blur) e.currentTarget.blur();
    if (wallet.nostrOrdinalsAddress == '') {
      setMintAfterConnect(true);
      setShowConnect(true);
      return;
    }
    openMintForm();
  };

  useEffect(() => {
    if (!mintAfterConnect) return;
    if (wallet.nostrOrdinalsAddress == '') return;
    setMintAfterConnect(false);
    setShowConnect(false);
    openMintForm();
  }, [mintAfterConnect, wallet.nostrOrdinalsAddress]);

  useEffect(() => {
    const cleanups = [];

    // ===== eyes-cinematic-motion-js =====
    (() => {
      const reduce = window.matchMedia(
        '(prefers-reduced-motion: reduce)',
      ).matches;
      const hero = document.querySelector('.hero');
      const ord = document.querySelector('#why-ordinals');

      // This script also added the header's glass-on-scroll; that part now
      // lives in EyesNav, which every page using the header shares.

      if (reduce) return;

      // Elegant staggered section reveals.
      const selectors = [
        '.ey',
        '.title',
        '.copy',
        '.bounty-card',
        '.news-clipping',
        '.clipping-card',
        '.ordinal-point',
        '.faq-item',
      ];
      const els = [...document.querySelectorAll(selectors.join(','))];
      els.forEach((el, i) => {
        el.classList.add('reveal-motion');
        if (i % 4 === 1) el.classList.add('reveal-delay-1');
        if (i % 4 === 2) el.classList.add('reveal-delay-2');
        if (i % 4 === 3) el.classList.add('reveal-delay-3');
      });
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) {
              e.target.classList.add('is-visible');
              io.unobserve(e.target);
            }
          });
        },
        { threshold: 0.1, rootMargin: '0px 0px -35px 0px' },
      );
      els.forEach((el) => io.observe(el));
      cleanups.push(() => io.disconnect());

      // Draw Why Ordinals line and illuminate symbols once.
      if (ord) {
        const oi = new IntersectionObserver(
          (es) => {
            es.forEach((e) => {
              if (e.isIntersecting) {
                ord.classList.add('ordinals-live');
                oi.disconnect();
              }
            });
          },
          { threshold: 0.28 },
        );
        oi.observe(ord);
        cleanups.push(() => oi.disconnect());
      }

      // Hero parallax + restrained cursor glow.
      if (hero) {
        hero.classList.add('hero-parallax-active');
        const glow = document.createElement('span');
        glow.className = 'hero-cursor-glow';
        hero.appendChild(glow);

        const onMove = (e) => {
          const r = hero.getBoundingClientRect();
          glow.style.left = e.clientX - r.left + 'px';
          glow.style.top = e.clientY - r.top + 'px';
        };
        const onLeave = () => {
          glow.style.opacity = '.12';
        };
        const onEnter = () => {
          glow.style.opacity = '.42';
        };
        const parallax = () => {
          const r = hero.getBoundingClientRect();
          const shift = Math.max(-28, Math.min(28, -r.top * 0.055));
          hero.style.setProperty('--hero-shift', shift + 'px');
        };

        hero.addEventListener('mousemove', onMove);
        hero.addEventListener('mouseleave', onLeave);
        hero.addEventListener('mouseenter', onEnter);
        parallax();
        window.addEventListener('scroll', parallax, { passive: true });

        cleanups.push(() => {
          hero.removeEventListener('mousemove', onMove);
          hero.removeEventListener('mouseleave', onLeave);
          hero.removeEventListener('mouseenter', onEnter);
          window.removeEventListener('scroll', parallax);
          glow.remove();
        });
      }
    })();

    // ===== exact-original-glitch-js =====
    (() => {
      const canvas = document.querySelector('.mint-glitch-original-canvas');
      const text = document.querySelector('.mint-glitch-original');
      if (!canvas || !text) return;
      const ctx = canvas.getContext('2d');

      const sizeCanvas = () => {
        canvas.width = text.offsetWidth;
        canvas.height = text.offsetHeight;
      };

      sizeCanvas();
      window.addEventListener('resize', sizeCanvas);

      const timer = setInterval(() => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        for (let x = 0; x < canvas.width; x += 6) {
          if (Math.random() > 0.85) {
            const y = 6 * Math.floor(Math.random() * (canvas.height / 6));
            const colors = ['#00ffff', '#ff0044', '#e53935', '#ffffff'];
            ctx.fillStyle = colors[Math.floor(Math.random() * colors.length)];
            ctx.globalAlpha = 0.6 * Math.random() + 0.2;
            ctx.fillRect(x, y, 6 * (Math.floor(4 * Math.random()) + 1), 6);
          }
        }
      }, 80);

      cleanups.push(() => {
        clearInterval(timer);
        window.removeEventListener('resize', sizeCanvas);
      });
    })();


    // The in-page anchors are reached from /watch as `/#section`, which lands
    // here before this tree exists, so the browser cannot make the jump.
    if (window.location.hash) {
      const target = document.querySelector(window.location.hash);
      if (target) target.scrollIntoView();
    }

    return () => cleanups.forEach((fn) => fn());
  }, []);

  const page = createPortal(
    <>
      <div className="site-smoke" aria-hidden="true">
        {' '}
        <span className="smoke s1">
        </span>
        <span className="smoke s2">
        </span>
        <span className="smoke s3">
        </span>
        <span className="smoke s4">
        </span>
        {' '}
      </div>
      {' '}
      <EyesNav home onMint={handleMintClick} />
      {' '}
      <header className="hero" id="about">
        <div className="hero-eye-glows" aria-hidden="true">
          <span className="hero-eye eye-left">
          </span>
          <span className="hero-eye eye-right">
          </span>
        </div>
        <div className="wrap heroGrid">
          <div>
            <div className="ey">
              FREE TO MINT 21K BITCOIN ORDINAL COLLECTION
            </div>
            <h1>
              THE EYES
              <br />
              {"ARE "}
              <span className="hero-red-word">
                ALWAYS
              </span>
              <br />
              <span className="hero-red-word">
                WATCHING
              </span>
            </h1>
            <p>
              21,000 unique Ordinals created by Mr. Black.
              <br />
              A message for the Bitcoin golden age.
              <br />
              Fear God, not man.
            </p>
            <div className="acts">
              <a className="btn r" href="#mint">
                MINT NOW
              </a>
              <a className="btn" href="#gallery">
                VIEW COLLECTION
              </a>
            </div>
            <div className="facts">
              <div className="fact">
                <i>
                  ₿
                </i>
                <span>
                  <b>
                    ON BITCOIN
                  </b>
                  Inscribed forever on the world's strongest network.
                </span>
              </div>
              <div className="fact">
                <i>
                  ◉
                </i>
                <span>
                  <b>
                    21,000 UNIQUE
                  </b>
                  Handcrafted art. No two the same.
                </span>
              </div>
              <div className="fact">
                <i>
                  ✣
                </i>
                <span>
                  <b>
                    A HIGHER MESSAGE
                  </b>
                  Art, story and a community built to last.
                </span>
              </div>
            </div>
          </div>
        </div>
      </header>
      {' '}
      <div className="wrap mint" id="mint">
        <div>
          <div className="ey">
            Mint is live
          </div>
          <div className="num">
            <span className="mint-glitch-stage">
              <span className="mint-glitch-original" data-text={remaining.toLocaleString('en-US')}>
                {remaining.toLocaleString('en-US')}
              </span>
              <canvas className="mint-glitch-original-canvas">
              </canvas>
            </span>
          </div>
          <div className="sub">
            MINTS UNTIL THE BITCOIN GOLDEN AGE BEGINS ✹
          </div>
          <div className="bar">
            <span style={{ width: `${mintedPct}%` }}>
            </span>
          </div>
          <div className="labels">
            <span>
              {minted.toLocaleString('en-US')} MINTED
            </span>
            <span>
              {flow.deployLimit.toLocaleString('en-US')} TOTAL SUPPLY
            </span>
          </div>
          <a className="btn r" href="/mint" onClick={handleMintClick}>
            MINT NOW
          </a>
        </div>
      </div>
      {' '}
      <section id="lore">
        <div className="wrap story">
          <div>
            <div className="ey">
              THE PROPHECY
            </div>
            <h2 className="title">
              {"A NEW AGE "}
              <span className="humanity-red">
                FOR HUMANITY.
              </span>
            </h2>
            <p className="copy">
              A new chapter is beginning. Technology, belief and human ambition are converging in ways that will reshape how we see the world and our place within it. The Eyes exist as a permanent record of this moment.
            </p>
            <a className="btn" href="/mint" onClick={handleMintClick}>
              MINT NOW
            </a>
          </div>
          <div>
            <div className="prophecy-video">
              <div className="prophecy-video-preview" aria-label="Video preview">
                {' '}
                <img src="/eyes/assets/88ff6f06d7.png" alt="Mr. Black & The Prophecy video thumbnail" />
                {' '}
                <div className="prophecy-play" aria-hidden="true">
                  <span>
                  </span>
                </div>
                {' '}
              </div>
            </div>
            <div className="cap">
              <div>
                <b>
                  MR. BLACK & THE PROPHECY
                </b>
                <small>
                  A Story About God, Bitcoin & The Future of Humanity
                </small>
              </div>
            </div>
          </div>
        </div>
      </section>
      {' '}
      <section id="bounty" className="bounty-section">
        {' '}
        <div className="wrap bounty-wrap">
          {' '}
          <div className="bounty-intro">
            {' '}
            <div className="ey">
              THE LEGENDS BOUNTY HUNT
            </div>
            {' '}
            <h2 className="title bounty-title">
              <span className="red">
                $100,000 IN BTC.
              </span>
              <br />
              HIDDEN IN THE EYES.
            </h2>
            {' '}
            <p className="copy bounty-copy">
              {"Hidden among the 21,000 Eyes are 120 one-of-one Legends inspired by icons of culture, history and myth. "}
              <b>
                44 of them carry Bitcoin bounties.
              </b>
            </p>
            {' '}
          </div>
          {' '}
          <div className="most-wanted-head">
            {' '}
            <div>
              {' '}
              <div className="ey">
                THE MOST WANTED
              </div>
              {' '}
              <h3>
                {"4 LEGENDS. "}
                <span className="red">
                  $5,000 EACH.
                </span>
              </h3>
              {' '}
            </div>
            {' '}
            <div className="bounty-total">
              ₿ BTC BOUNTIES
            </div>
            {' '}
          </div>
          {' '}
          <div className="wanted-grid">
            {' '}
            <div className="wanted-card">
              <span>
                01
              </span>
              <div className="wanted-image">
                <img src="/eyes/assets/08fc54a921.png" alt="MR. BLACK" />
              </div>
              <b>
                MR. BLACK
              </b>
              <small>
                $5,000 BTC BOUNTY
              </small>
            </div>
            {' '}
            <div className="wanted-card">
              <span>
                02
              </span>
              <div className="wanted-image">
                <img src="/eyes/assets/423cca6450.png" alt="HEROIC LEADER" />
              </div>
              <b>
                HEROIC LEADER
              </b>
              <small>
                $5,000 BTC BOUNTY
              </small>
            </div>
            {' '}
            <div className="wanted-card">
              <span>
                03
              </span>
              <div className="wanted-image">
                <img src="/eyes/assets/4624407d4f.png" alt="SON OF GOD" />
              </div>
              <b>
                SON OF GOD
              </b>
              <small>
                $5,000 BTC BOUNTY
              </small>
            </div>
            {' '}
            <div className="wanted-card">
              <span>
                04
              </span>
              <div className="wanted-image">
                <img src="/eyes/assets/f38b06879a.png" alt="WENT TO IBIZA" />
              </div>
              <b>
                WENT TO IBIZA
              </b>
              <small>
                $5,000 BTC BOUNTY
              </small>
            </div>
            {' '}
          </div>
          {' '}
          <div className="bounty-bottom">
            {' '}
            <div className="bounty-more">
              {' '}
              <div className="ey">
                AND 40 MORE
              </div>
              {' '}
              <h3>
                {"40 HIDDEN LEGENDS. "}
                <span className="red">
                  $2,000 EACH.
                </span>
              </h3>
              {' '}
              <p>
                You won’t know which Eye you’re getting until you mint. Find one of the 44 winning Legends and its BTC bounty is sent directly to your wallet.
              </p>
              {' '}
            </div>
            {' '}
            <div className="hunt-callout">
              {' '}
              <b>
                44 WINNING EYES.
              </b>
              {' '}
              <b>
                $100,000 IN BITCOIN.
              </b>
              {' '}
              <span>
                THE HUNT IS ON.
              </span>
              {' '}
            </div>
            {' '}
          </div>
          {' '}
          <div className="bounty-actions">
            {' '}
            <a className="btn r" href="#mint">
              MINT NOW
            </a>
            {' '}
            <a className="btn" href="#">
              JOIN THE DISCORD FOR MORE INFO
            </a>
            {' '}
          </div>
          {' '}
        </div>
        {' '}
      </section>
      {' '}
      <section id="gallery" className="viewer-section">
        {' '}
        <div className="wrap viewer-wrap">
          {' '}
          <div className="viewer-intro">
            {' '}
            <div className="ey">
              THE ARTWORK VIEWER
            </div>
            {' '}
            <h2>
              {"EXPLORE THE "}
              <span className="red">
                EYES.
              </span>
            </h2>
            {' '}
            <p>
              Browse minted Eyes, discover Legends and search the collection by Eye number, name, inscription or wallet address.
            </p>
            {' '}
          </div>
          {' '}
          <EyeViewer />
          {' '}
          <div className="viewer-loading" id="viewerLoading">
            {' '}
            <span>
            </span>
            <span>
            </span>
            <span>
            </span>
            {' '}
            <b>
              LOADING MORE EYES
            </b>
            {' '}
          </div>
          {' '}
        </div>
        {' '}
      </section>
      {' '}
      <section id="why-ordinals" className="ordinals-horizontal">
        {' '}
        <div className="wrap ordinals-horizontal-wrap">
          {' '}
          <div className="ordinals-horizontal-head">
            {' '}
            <div className="ey">
              BUILT ON BITCOIN
            </div>
            {' '}
            <h2>
              {"WHY "}
              <span className="red">
                ORDINALS?
              </span>
            </h2>
            {' '}
            <p>
              Digital art that doesn't depend on us to survive.
            </p>
            {' '}
          </div>
          {' '}
          <div className="ordinal-flow">
            {' '}
            <div className="ordinal-flow-line">
            </div>
            {' '}
            <article className="ordinal-point">
              {' '}
              <div className="ordinal-symbol">
                {' '}
                <svg viewBox="0 0 64 64" aria-hidden="true">
                  {' '}
                  <circle cx="32" cy="32" r="21">
                  </circle>
                  {' '}
                  <path d="M25 19v26M36 17v29M22 24h18c5 0 8 3 8 7s-3 7-8 7H22M24 31h17">
                  </path>
                  {' '}
                </svg>
                {' '}
              </div>
              {' '}
              <span>
                INSCRIBED
              </span>
              {' '}
              <h3>
                ON BITCOIN
              </h3>
              {' '}
              <p>
                Each Eye is inscribed directly onto Bitcoin.
              </p>
              {' '}
            </article>
            {' '}
            <article className="ordinal-point">
              {' '}
              <div className="ordinal-symbol">
                {' '}
                <svg viewBox="0 0 64 64" aria-hidden="true">
                  {' '}
                  <path d="M18 32c0-8 5-14 13-14 9 0 12 9 17 14-5 5-8 14-17 14-8 0-13-6-13-14Z">
                  </path>
                  {' '}
                  <path d="M46 32c0-8-5-14-13-14-9 0-12 9-17 14 5 5 8 14 17 14 8 0 13-6 13-14Z">
                  </path>
                  {' '}
                </svg>
                {' '}
              </div>
              {' '}
              <span>
                PERMANENT
              </span>
              {' '}
              <h3>
                NO EXPIRY
              </h3>
              {' '}
              <p>
                No hosting bill. No platform required. No delete button.
              </p>
              {' '}
            </article>
            {' '}
            <article className="ordinal-point">
              {' '}
              <div className="ordinal-symbol">
                {' '}
                <svg viewBox="0 0 64 64" aria-hidden="true">
                  {' '}
                  <rect x="13" y="20" width="38" height="29" rx="6">
                  </rect>
                  {' '}
                  <path d="M13 27h38M39 34h12v9H39a4.5 4.5 0 0 1 0-9Z">
                  </path>
                  {' '}
                  <circle cx="42" cy="38.5" r="1.5">
                  </circle>
                  {' '}
                </svg>
                {' '}
              </div>
              {' '}
              <span>
                OWNED
              </span>
              {' '}
              <h3>
                IN YOUR WALLET
              </h3>
              {' '}
              <p>
                Your Eye is held and verified through Bitcoin itself.
              </p>
              {' '}
            </article>
            {' '}
            <article className="ordinal-point">
              {' '}
              <div className="ordinal-symbol eye-symbol">
                {' '}
                <svg viewBox="0 0 64 64" aria-hidden="true">
                  {' '}
                  <path d="M7 32s9-14 25-14 25 14 25 14-9 14-25 14S7 32 7 32Z">
                  </path>
                  {' '}
                  <circle cx="32" cy="32" r="8">
                  </circle>
                  {' '}
                  <circle cx="32" cy="32" r="2">
                  </circle>
                  {' '}
                </svg>
                {' '}
              </div>
              {' '}
              <span>
                FOREVER
              </span>
              {' '}
              <h3>
                BUILT TO OUTLIVE US
              </h3>
              {' '}
              <p>
                Art preserved as a permanent record of this moment.
              </p>
              {' '}
            </article>
            {' '}
          </div>
          {' '}
          <div className="ordinal-horizontal-end">
            {' '}
            <strong>
              {"21,000 EYES. "}
              <em>
                FOREVER ON BITCOIN.
              </em>
            </strong>
            {' '}
          </div>
          {' '}
        </div>
        {' '}
      </section>
      {' '}
      <section id="news" className="clippings-news">
        {' '}
        <div className="wrap clippings-wrap">
          {' '}
          <div className="clippings-head">
            {' '}
            <div className="ey">
              PRESS & MEDIA
            </div>
            {' '}
            <h2>
              {"FROM THE "}
              <span className="red">
                HEADLINES.
              </span>
            </h2>
            {' '}
            <p>
              Selected stories, features and coverage surrounding Mr. Black and The Eyes.
            </p>
            {' '}
          </div>
          {' '}
          <div className="clippings-board">
            <a className="clip clip-1" style={{'--rot': '-1.1deg'}} href="https://decrypt.co/240924/mr-black-to-unveil-groundbreaking-21000-piece-ordinal-collection-on-bitcoin" target="_blank" rel="noopener">
              {' '}
              <div className="clip-mast">
                DECRYPT
              </div>
              {' '}
              <div className="clip-rule">
              </div>
              {' '}
              <div className="clip-meta">
                <span>
                  JUL 2024
                </span>
                <span>
                  PRESS CLIPPING
                </span>
              </div>
              {' '}
              <h3>
                Mr. Black to Unveil Groundbreaking 21,000-Piece Ordinal Collection on Bitcoin
              </h3>
              {' '}
              <p>
                Read the full story online.
              </p>
              {' '}
              <div className="clip-read">
                {"CONTINUE READING "}
                <b>
                  ↗
                </b>
              </div>
              {' '}
            </a>
            <a className="clip clip-2" style={{'--rot': '.7deg'}} href="https://resident.com/business-and-finance/2024/07/25/groundbreaking-work-from-renowned-artist-mr-black-to-be-unveiled-on-x-with-21000-piece-ordinal-collection-on-bitcoin" target="_blank" rel="noopener">
              {' '}
              <div className="clip-mast">
                RESIDENT
              </div>
              {' '}
              <div className="clip-rule">
              </div>
              {' '}
              <div className="clip-meta">
                <span>
                  JUL 2024
                </span>
                <span>
                  PRESS CLIPPING
                </span>
              </div>
              {' '}
              <h3>
                Groundbreaking Work From Renowned Artist Mr. Black
              </h3>
              {' '}
              <p>
                Read the full story online.
              </p>
              {' '}
              <div className="clip-read">
                {"CONTINUE READING "}
                <b>
                  ↗
                </b>
              </div>
              {' '}
            </a>
            <a className="clip clip-3" style={{'--rot': '-.45deg'}} href="https://t2conline.com/mr-black-is-back-and-this-time-in-times-square/" target="_blank" rel="noopener">
              {' '}
              <div className="clip-mast">
                TIMES SQUARE CHRONICLES
              </div>
              {' '}
              <div className="clip-rule">
              </div>
              {' '}
              <div className="clip-meta">
                <span>
                  PRESS
                </span>
                <span>
                  PRESS CLIPPING
                </span>
              </div>
              {' '}
              <h3>
                Mr. Black Is Back and This Time in Times Square
              </h3>
              {' '}
              <p>
                Read the full story online.
              </p>
              {' '}
              <div className="clip-read">
                {"CONTINUE READING "}
                <b>
                  ↗
                </b>
              </div>
              {' '}
            </a>
            <a className="clip clip-4" style={{'--rot': '1deg'}} href="https://www.binance.com/en/square/post/26354530425794" target="_blank" rel="noopener">
              {' '}
              <div className="clip-mast">
                BINANCE SQUARE
              </div>
              {' '}
              <div className="clip-rule">
              </div>
              {' '}
              <div className="clip-meta">
                <span>
                  BITCOIN
                </span>
                <span>
                  PRESS CLIPPING
                </span>
              </div>
              {' '}
              <h3>
                The Eyes Are Always Watching
              </h3>
              {' '}
              <p>
                Read the full story online.
              </p>
              {' '}
              <div className="clip-read">
                {"CONTINUE READING "}
                <b>
                  ↗
                </b>
              </div>
              {' '}
            </a>
            <a className="clip clip-5" style={{'--rot': '-.75deg'}} href="https://www.law.com/dailybusinessreview/2025/01/31/crypto-artist-sues-over-alleged-fraud-in-5-million-elon-musk-scheme/" target="_blank" rel="noopener">
              {' '}
              <div className="clip-mast">
                LAW.COM
              </div>
              {' '}
              <div className="clip-rule">
              </div>
              {' '}
              <div className="clip-meta">
                <span>
                  JAN 2025
                </span>
                <span>
                  PRESS CLIPPING
                </span>
              </div>
              {' '}
              <h3>
                Crypto Artist Sues Over Alleged Fraud in $5 Million Elon Musk Scheme
              </h3>
              {' '}
              <p>
                Read the full story online.
              </p>
              {' '}
              <div className="clip-read">
                {"CONTINUE READING "}
                <b>
                  ↗
                </b>
              </div>
              {' '}
            </a>
            <a className="clip clip-6" style={{'--rot': '.45deg'}} href="https://www.fshnmagazine.com/shades-of-opportunites-in-mr-black-business/" target="_blank" rel="noopener">
              {' '}
              <div className="clip-mast">
                FSHN MAGAZINE
              </div>
              {' '}
              <div className="clip-rule">
              </div>
              {' '}
              <div className="clip-meta">
                <span>
                  FEATURE
                </span>
                <span>
                  PRESS CLIPPING
                </span>
              </div>
              {' '}
              <h3>
                Shades of Opportunities in Mr. Black Business
              </h3>
              {' '}
              <p>
                Read the full story online.
              </p>
              {' '}
              <div className="clip-read">
                {"CONTINUE READING "}
                <b>
                  ↗
                </b>
              </div>
              {' '}
            </a>
            <a className="clip clip-7" style={{'--rot': '-.3deg'}} href="https://magazine.atavist.com/2025/all-that-glitters-jona-rechnitz-lawsuit-jadelle-jewelry-coba-ethereummax-mayweather" target="_blank" rel="noopener">
              {' '}
              <div className="clip-mast">
                THE ATAVIST
              </div>
              {' '}
              <div className="clip-rule">
              </div>
              {' '}
              <div className="clip-meta">
                <span>
                  2025
                </span>
                <span>
                  PRESS CLIPPING
                </span>
              </div>
              {' '}
              <h3>
                All That Glitters
              </h3>
              {' '}
              <p>
                Read the full story online.
              </p>
              {' '}
              <div className="clip-read">
                {"CONTINUE READING "}
                <b>
                  ↗
                </b>
              </div>
              {' '}
            </a>
            <a className="clip clip-8" style={{'--rot': '.8deg'}} href="https://www.youtube.com/watch?v=_vAdfCKnRO4" target="_blank" rel="noopener">
              {' '}
              <div className="clip-mast">
                WATCH
              </div>
              {' '}
              <div className="clip-rule">
              </div>
              {' '}
              <div className="clip-meta">
                <span>
                  VIDEO
                </span>
                <span>
                  PRESS CLIPPING
                </span>
              </div>
              {' '}
              <h3>
                Mr. Black on YouTube
              </h3>
              {' '}
              <p>
                Read the full story online.
              </p>
              {' '}
              <div className="clip-read">
                {"CONTINUE READING "}
                <b>
                  ↗
                </b>
              </div>
              {' '}
            </a>
          </div>
          {' '}
        </div>
        {' '}
      </section>
      {' '}
      <section id="faq" className="faq-section">
        <div className="wrap faq-wrap">
          {' '}
          <div className="faq-head">
            <div className="ey">
              FAQ
            </div>
            <h2 className="title">
              {"EVERYTHING YOU "}
              <span className="red">
                NEED TO KNOW.
              </span>
            </h2>
            <p className="copy">
              Minting, wallets, inscriptions and everything in between.
            </p>
          </div>
          {' '}
          <div className="faq-list">
            <details className="faq-item">
              <summary>
                <span>
                  What is The Eyes Are Always Watching?
                </span>
                <b>
                  +
                </b>
              </summary>
              <div className="faq-answer">
                <p>
                  21,000 unique artworks inscribed permanently on Bitcoin using Ordinals, created by Mr. Black.
                </p>
              </div>
            </details>
            <details className="faq-item">
              <summary>
                <span>
                  Is it really free to mint?
                </span>
                <b>
                  +
                </b>
              </summary>
              <div className="faq-answer">
                <p>
                  Yes. The mint is 100% free. You only pay the Bitcoin network inscription fee, typically around $7–$20.
                </p>
              </div>
            </details>
            <details className="faq-item">
              <summary>
                <span>
                  What wallet can I use?
                </span>
                <b>
                  +
                </b>
              </summary>
              <div className="faq-answer">
                <p>
                  UniSat, Xverse or OKX Wallet.
                </p>
              </div>
            </details>
            <details className="faq-item">
              <summary>
                <span>
                  What address type do I need?
                </span>
                <b>
                  +
                </b>
              </summary>
              <div className="faq-answer">
                <p>
                  Use a Taproot address, which starts with bc1p.
                </p>
              </div>
            </details>
            <details className="faq-item">
              <summary>
                <span>
                  Where do I mint?
                </span>
                <b>
                  +
                </b>
              </summary>
              <div className="faq-answer">
                <p>
                  {"This is the only official mint page: "}
                  <a href="https://www.eyes4384.com/" target="_blank" rel="noopener">
                    eyes4384.com ↗
                  </a>
                </p>
              </div>
            </details>
            <details className="faq-item">
              <summary>
                <span>
                  How long does minting take?
                </span>
                <b>
                  +
                </b>
              </summary>
              <div className="faq-answer">
                <p>
                  Usually 10–60 minutes, depending on Bitcoin network congestion and the fee selected.
                </p>
              </div>
            </details>
            <details className="faq-item">
              <summary>
                <span>
                  Where can I see my Eye?
                </span>
                <b>
                  +
                </b>
              </summary>
              <div className="faq-answer">
                <p>
                  Your Eye will appear in your wallet once confirmed. Our Discord bot also automatically detects every mint and posts an official Ordinals.com link where you can view your inscription.
                </p>
              </div>
            </details>
            <details className="faq-item">
              <summary>
                <span>
                  Where can I trade?
                </span>
                <b>
                  +
                </b>
              </summary>
              <div className="faq-answer">
                <p>
                  Satflow.
                </p>
              </div>
            </details>
            <details className="faq-item">
              <summary>
                <span>
                  Still have questions?
                </span>
                <b>
                  +
                </b>
              </summary>
              <div className="faq-answer">
                <p>
                  Ask in Discord and a member of the team will be happy to help!
                </p>
              </div>
            </details>
          </div>
        </div>
      </section>
      {' '}
      <EyesFooter home />
    </>,
    document.body,
  );

  return (
    <>
      {page}
      <MintDialogs flow={flow} />
      {showConnect ? (
        <Connect open={showConnect} onOpenChange={setShowConnect} hideTrigger />
      ) : null}
    </>
  );
}
